import { useDeepLinks } from "src/hooks/useDeepLinks";
import {
  BoardItem,
  ItemType,
  QueryItem,
  ReportItem,
  SpaceItem,
  TopicItem,
} from "./useLibrary";
import { ToasterMessageType, useToaster } from "src/hooks/useToaster";
import { LoadingOutlined } from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import { useSpacesQuery } from "pages/[orgId]/space/useSpacesQuery";
import { FavoriteIcon } from "./FavoriteIcon";
import { DeepLinkType } from "pages/api/deep-links/types";
import { useEffect, useState } from "react";
import { CustomTooltip } from "./CustomTooltip";
const isFavorited = (
  item: BoardItem | QueryItem | TopicItem | ReportItem | SpaceItem
) => {
  if (item.itemType === ItemType.SPACES) {
    return (item as SpaceItem).pin_to_nav;
  }
  return item.isBookmarked;
};

export function FavoriteButton({
  item,
  deeplinkQuery: providedDeeplinkQuery,
  spacesQuery: providedSpacesQuery,
}: {
  item: BoardItem | QueryItem | TopicItem | ReportItem | SpaceItem;
  deeplinkQuery?: ReturnType<typeof useDeepLinks>;
  spacesQuery?: ReturnType<typeof useSpacesQuery>;
}) {
  const toaster = useToaster();
  const [isBookmarked, setIsBookmarked] = useState(isFavorited(item));

  useEffect(() => {
    setIsBookmarked(isFavorited(item));
  }, [item]);

  const itemTypeToDeepLinkType = {
    [ItemType.BOARDS]: DeepLinkType.BOARD,
    [ItemType.QUERIES]: DeepLinkType.EXPLORE,
    [ItemType.TOPICS]: DeepLinkType.DIGEST_2,
    [ItemType.REPORTS]: DeepLinkType.NARRATIVE,
    [ItemType.SPACES]: DeepLinkType.SPACE,
  } as const;

  const localDeeplinkQuery = useDeepLinks(
    itemTypeToDeepLinkType[item.itemType]
  );
  const deeplinkQuery = providedDeeplinkQuery ?? localDeeplinkQuery;

  if (item.itemType === ItemType.SPACES) {
    return (
      <SpaceButton
        item={item as unknown as SpaceItem}
        spacesQuery={providedSpacesQuery}
      />
    );
  }

  return (
    <CustomTooltip
      content={isBookmarked ? "Remove from favorites" : "Add to favorites"}
      placement="top"
    >
      <Button
        type="text"
        size="small"
        icon={
          deeplinkQuery.addBookmark.isLoading ||
          deeplinkQuery.removeBookmark.isLoading ? (
            <LoadingOutlined />
          ) : (
            <FavoriteIcon filled={isBookmarked} />
          )
        }
        disabled={!item}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (!item) return;
          const method = item.isBookmarked ? "removeBookmark" : "addBookmark";
          deeplinkQuery[method]
            .mutateAsync({ id: item.id })
            .then(() => {
              toaster.addMessage(ToasterMessageType.SUCCESS, {
                message: `${item.name} ${
                  item.isBookmarked ? "removed from" : "added to"
                } favorites`,
              });
              setIsBookmarked(!isBookmarked);
            })
            .catch(() => {
              toaster.addMessage(ToasterMessageType.ERROR, {
                message: "There was an unexpected error. Please try again.",
              });
            });
        }}
      />
    </CustomTooltip>
  );
}

function SpaceButton(props: {
  item: SpaceItem;
  spacesQuery?: ReturnType<typeof useSpacesQuery>;
}) {
  const { item, spacesQuery } = props;
  const { pinSpace } = useSpacesQuery(item.id);
  const [isBookmarked, setIsBookmarked] = useState(isFavorited(item));

  const space = spacesQuery?.pinSpace || pinSpace;

  return (
    <Tooltip
      title={isBookmarked ? "Remove from favorites" : "Add to favorites"}
    >
      <Button
        type="text"
        size="small"
        icon={<FavoriteIcon filled={isBookmarked} />}
        disabled={!item}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (!item) return;
          space.mutate({
            action: isBookmarked ? "remove" : "add",
            space_id: item.uuid,
          });
          setIsBookmarked(!isBookmarked);
        }}
      />
    </Tooltip>
  );
}
